<template>
  <div>
    <div class="text-center">
      <v-dialog v-model="dialogImportKML" width="500">
        <v-card>
          <v-card-title class="text-headline-small bg-grey-lighten-2"> Importation des coordonnées KML </v-card-title>
          <v-alert border="bottom" type="warning">
            ATTENTION! Cette action supprimera les coordonées de la section actuelles.
          </v-alert>
          <v-card-text>
            <v-textarea variant="solo" v-model="importKMLText" label="Insérer les coordonnées ici"></v-textarea>
            <v-btn color="primary" class="ma-2 text-white" @click="ImportKML()" :disabled="importKMLText.length == 0">
              Importer les données
            </v-btn>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="dialogImportKML = false"> Annuler l'action </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogMapAction" width="500">
        <v-card>
          <v-card-title class="text-headline-small bg-grey-lighten-2"> Quelle action souhaitez-vous apporter? </v-card-title>

          <v-card-text>
            <v-btn variant="text" color="primary" class="ma-2 text-white" @click="addMarker('putin')"> Ajouter un Put In </v-btn>
          </v-card-text>
          <v-card-text>
            <v-btn variant="text" color="primary" class="ma-2 text-white" @click="addMarker('takeout')">
              Ajouter un Take Out
            </v-btn>
          </v-card-text>
          <v-card-text v-if="sectionPath.length < 2">
            <v-btn variant="text" color="primary" class="ma-2 text-white" @click="addSectionPath()"> Ajouter la section </v-btn>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="dialogMapAction = false"> Annuler l'action </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <v-fab-transition v-if="isModification">
      <v-btn v-hotkey="'ctrl+s'" color="green" @click="save" icon size="large" class="fab-fixed-bottom-right">
        <v-icon size="large">mdi-content-save-outline</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-container>
      <v-row>
        <v-col>
          <v-alert v-if="errorMessage" density="compact" variant="tonal" border="start" type="error">
            {{ errorMessage }}
          </v-alert>
          <v-alert v-if="isModification" density="compact" variant="tonal" border="start" type="warning">
            Modifications non sauvegardées. Sauvegardez (CTRL+S) avant de changer de section ou d'onglet.
          </v-alert>
        </v-col>
      </v-row>
      <v-row class="mb-4">
        <v-col>
          <v-switch v-model="section.direction_info" label="Activer la section Directions"></v-switch>
          <small class="text-medium-emphasis"> (L'activation de la section sera sauvegardée automatiquement)</small>
        </v-col>
        <v-col>
          <v-btn variant="flat" color="primary" @click="dialogImportKML = true"> Importer les positions KML </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-sheet :height="gmapHeight" elevation="8">
            <google-map
              v-if="mapsAvailable"
              ref="sectionMaps"
              :api-promise="mapsApiPromise"
              :center="mapOptions.center"
              :zoom="mapOptions.zoom"
              :map-type-id="mapOptions.mapTypeId"
              style="height: 100%"
              @click="setCursorInfo"
            >
              <map-marker
                v-for="(marker, markerIndex) in markers"
                :key="`marker-${markerIndex}`"
                :options="marker.options"
                @click="removeMarker(markerIndex)"
              />
              <map-polyline
                ref="sectionPolyline"
                :options="{ path: sectionPath, editable, strokeColor: 'dodgerblue', strokeWeight: 5 }"
                @mouseup="onSectionPathEdited"
              />
            </google-map>
          </v-sheet>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            :model-value="putinCoordinates"
            @change="putinCoordinates = $event.target.value"
            @click:clear="putinCoordinates = ''"
            label="Coordonnees Put In (lat,lng)"
            hint="Exemple: 46.12345,-72.54321"
            persistent-hint
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :model-value="takeoutCoordinates"
            @change="takeoutCoordinates = $event.target.value"
            @click:clear="takeoutCoordinates = ''"
            label="Coordonnees Take Out (lat,lng)"
            hint="Exemple: 46.12345,-72.54321"
            persistent-hint
            clearable
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import SectionsService from "@/store/modules/sections";
  import { GoogleMap, Marker as MapMarker, Polyline as MapPolyline } from "vue3-google-map";
  import { GmapsService } from "@/services/Gmaps.service";
  import { isGoogleMapsAvailable, loadGoogleMaps } from "@/services/GoogleMapsLoader.service";
  import { notify } from "@/services/Notification.service";

  export default {
    components: { GoogleMap, MapMarker, MapPolyline },
    props: {
      section: {
        type: Object,
        default: function () {
          return {};
        },
      },
      reloadData: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        gmaps_key: process.env.VUE_APP_GOOGLE_MAPS_KEY,
        mapsAvailable: isGoogleMapsAvailable(),
        mapsApiPromise: isGoogleMapsAvailable() ? loadGoogleMaps() : null,
        mapOptions: {
          mapTypeId: "roadmap",
          center: GmapsService.BASE_CENTER_POSITION,
          zoom: 12,
        },
        directions: {},
        markers: [],
        sectionPath: [],
        editable: true,
        isModification: false,
        errorMessage: "",
        valid: true,
        dialogMapAction: false,
        dialogImportKML: false,
        importKMLText: "",
        currentCursorInfo: {},
      };
    },
    async created() {
      await this.getDirections();
      //this.watchMarkers();
    },
    mounted() {
      if (typeof window !== "undefined") {
        window.addEventListener("resize", this.handleViewportChange, { passive: true });
      }
    },
    beforeUnmount() {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", this.handleViewportChange);
      }
    },
    async updated() {
      if (this.reloadData) {
        await this.getDirections();
        //this.watchMarkers();
        this.$emit("update", false);
      }
    },
    computed: {
      gmapHeight() {
        switch (this.$vuetify.display.name) {
          case "xs":
            return "220px";
          case "sm":
            return "400px";
          case "md":
            return "500px";
          case "lg":
            return "600px";
          case "xl":
            return "800px";
        }
        return "400px";
      },
      putinCoordinates: {
        get() {
          return this.getMarkerCoordinatesByType("putin");
        },
        set(value) {
          this.setMarkerCoordinatesByType("putin", value);
        },
      },
      takeoutCoordinates: {
        get() {
          return this.getMarkerCoordinatesByType("takeout");
        },
        set(value) {
          this.setMarkerCoordinatesByType("takeout", value);
        },
      },
    },
    methods: {
      handleViewportChange() {
        if (this.markers.length > 0) this.setCenterPosition();
      },
      getMapViewportSize() {
        let width = 0;
        let height = 0;

        if (this.$refs.sectionMaps && this.$refs.sectionMaps.$el) {
          width = this.$refs.sectionMaps.$el.clientWidth || 0;
          height = this.$refs.sectionMaps.$el.clientHeight || 0;
        }

        if (typeof window !== "undefined") {
          width = width || window.innerWidth || 640;
          height = height || Math.round((window.innerHeight || 600) * 0.6);
        }

        return { width, height };
      },
      async getDirections() {
        this.directions = await SectionsService.actions.findSectionsDirections(this.section.id);
        await this.waitForMapsApi();
        this.directions.putin = this.directions.putin || "";
        this.directions.takeout = this.directions.takeout || "";
        this.setMarkers();
        this.setSectionPath();
      },
      watchMarkers() {
        this.markers.forEach((marker) => {
          this.$watch(() => marker, this.handleChange(), { deep: true });
        });
      },
      watchSectionPath() {
        this.sectionPath.forEach((sectionPath) => {
          this.$watch(() => sectionPath, this.handleChange(), { deep: true });
        });
      },
      getMarkerIndexByType(type) {
        return this.markers.findIndex((marker) => {
          const markerType = marker && marker.type ? marker.type : "";
          const markerLabel = marker && marker.options && marker.options.label ? marker.options.label.text : "";
          if (type === "putin") {
            return markerType === "putin" || markerLabel === "Put In";
          }
          return markerType === "takeout" || markerLabel === "Take Out";
        });
      },
      getMarkerCoordinatesByType(type) {
        const markerIndex = this.getMarkerIndexByType(type);
        if (markerIndex === -1) {
          return "";
        }

        const marker = this.markers[markerIndex];
        if (!marker || !marker.options || !marker.options.position) {
          return "";
        }

        const { lat, lng } = marker.options.position;
        if (typeof lat !== "number" || typeof lng !== "number") {
          return "";
        }

        return `${lat},${lng}`;
      },
      getMarkerPresentationByType(type) {
        if (type === "putin") {
          return {
            label: GmapsService.LABEL_PUTIN,
            icon: GmapsService.SVG_PUTIN,
          };
        }

        return {
          label: GmapsService.LABEL_TAKEOUT,
          icon: GmapsService.SVG_TAKEOUT,
        };
      },
      upsertMarker(type, position) {
        const markerIndex = this.getMarkerIndexByType(type);
        const { label, icon } = this.getMarkerPresentationByType(type);
        const marker = {
          type,
          options: {
            position,
            label,
            icon,
          },
        };

        if (markerIndex === -1) {
          this.markers.push(marker);
        } else {
          this.markers[markerIndex] = marker;
        }
      },
      setMarkerCoordinatesByType(type, value) {
        const normalizedValue = value ? value.trim() : "";
        const markerIndex = this.getMarkerIndexByType(type);

        if (!normalizedValue.length) {
          if (markerIndex !== -1) {
            this.markers.splice(markerIndex, 1);
            this.handleChange();
            this.setCenterPosition();
          }
          return;
        }

        const latLng = GmapsService.getLatLngFromStringLatLng(normalizedValue);
        if (!latLng) {
          this.errorMessage = "Format invalide. Utilisez le format lat,lng (ex: 46.12345,-72.54321).";
          return;
        }

        this.upsertMarker(type, latLng);
        this.handleChange();
        this.setCenterPosition();
      },
      syncDirectionsFromMarkers() {
        this.directions.putin = this.getMarkerCoordinatesByType("putin");
        this.directions.takeout = this.getMarkerCoordinatesByType("takeout");
      },
      handleChange() {
        this.errorMessage = "";
        this.isModification = true;
      },
      setSectionPath() {
        this.sectionPath = [];
        if (this.directions.sectionPath) {
          if (this.directions.sectionPath.length > 0) this.sectionPath = JSON.parse(this.directions.sectionPath);
        }
      },
      setMarkers() {
        this.markers = [];

        if (!this.directions.markers) {
          this.getOldMarkers();
        } else if (this.directions.markers.length == 0) {
          this.getOldMarkers();
        } else {
          this.markers = JSON.parse(this.directions.markers);
        }

        this.getSVGsLabelOrigin();
        this.setCenterPosition();
      },
      setCenterPosition() {
        if (this.markers.length > 0) {
          const putInMarker = this.markers.find((marker) => marker.type === "putin");
          const takeOutMarker = this.markers.find((marker) => marker.type === "takeout");
          const fallbackPositions = this.markers.map((marker) => marker.options.position);
          const { width, height } = this.getMapViewportSize();

          const positionA = putInMarker
            ? `${putInMarker.options.position.lat},${putInMarker.options.position.lng}`
            : fallbackPositions[0]
            ? `${fallbackPositions[0].lat},${fallbackPositions[0].lng}`
            : "";
          const positionB = takeOutMarker
            ? `${takeOutMarker.options.position.lat},${takeOutMarker.options.position.lng}`
            : fallbackPositions[1]
            ? `${fallbackPositions[1].lat},${fallbackPositions[1].lng}`
            : "";
          const { center, zoom } = GmapsService.getCenterPosition(positionA, positionB, {
            viewportWidth: width,
            viewportHeight: height,
          });
          this.mapOptions.center = center;
          this.mapOptions.zoom = zoom;
        } else {
          this.mapOptions.center = GmapsService.BASE_CENTER_POSITION;
          this.mapOptions.zoom = 10;
        }
      },
      getOldMarkers() {
        if (this.directions.putin.length > 0) {
          const putinPosition = GmapsService.getLatLngFromStringLatLng(this.directions.putin);
          if (putinPosition) {
            this.upsertMarker("putin", putinPosition);
          }
        }

        if (this.directions.takeout.length > 0) {
          const takeoutPosition = GmapsService.getLatLngFromStringLatLng(this.directions.takeout);
          if (takeoutPosition) {
            this.upsertMarker("takeout", takeoutPosition);
          }
        }
      },
      getSVGsLabelOrigin() {
        if (!window.google || !window.google.maps || !window.google.maps.Point) return;
        var point = new window.google.maps.Point(12, 30);
        var svgPutin = GmapsService.SVG_PUTIN;
        var svgTakeout = GmapsService.SVG_TAKEOUT;
        svgPutin.labelOrigin = point;
        svgTakeout.labelOrigin = point;

        return { putin: svgPutin, takeout: svgTakeout };
      },
      // Marker styles use Maps classes (google.maps.Point), which exist only once the API has loaded.
      async waitForMapsApi() {
        if (!this.mapsApiPromise) return;
        try {
          await this.mapsApiPromise;
        } catch (error) {
          console.error("Unable to load Google Maps:", error);
        }
      },
      setCursorInfo($event) {
        this.currentCursorInfo = $event.latLng.toJSON();
        this.dialogMapAction = true;
      },
      addMarker(type) {
        if (type == "putin") this.upsertMarker("putin", this.currentCursorInfo);
        if (type == "takeout") this.upsertMarker("takeout", this.currentCursorInfo);
        this.dialogMapAction = false;
        this.setCenterPosition();
        this.handleChange();
      },
      removeMarker(index) {
        this.markers.splice(index, 1);
        this.setCenterPosition();
        this.handleChange();
      },
      addSectionPath() {
        this.sectionPath.push(this.currentCursorInfo);
        this.dialogMapAction = false;
        this.handleChange();
      },
      // Dragging or adding a vertex of the editable path ends with a mouseup on the polyline.
      onSectionPathEdited() {
        const polyline = this.$refs.sectionPolyline && this.$refs.sectionPolyline.polyline;
        if (!polyline) return;

        const editedPath = polyline
          .getPath()
          .getArray()
          .map((point) => point.toJSON());
        if (JSON.stringify(editedPath) !== JSON.stringify(this.sectionPath)) {
          this.updateSectionPath(editedPath);
        }
      },
      updateSectionPath(event) {
        this.sectionPath = event;
        this.handleChange();
      },
      removeSectionPath(index) {
        this.sectionPath.splice(index, 1);
        this.handleChange();
      },
      async save() {
        if (this.valid) {
          this.syncDirectionsFromMarkers();
          this.directions.markers = JSON.stringify(this.markers);
          this.directions.sectionPath = JSON.stringify(this.sectionPath);

          SectionsService.actions
            .setSectionsDirections(this.section.id, this.directions)
            .then(() => {
              this.isModification = false;
              notify("La section a été sauvegardée avec succès.");
            })
            .catch((error) => {
              console.log(error);
              this.errorMessage =
                "Une erreur s'est produite lors de la sauvegarde des directions, vérifiez les résultats et recommencez.";
            });
        } else {
          this.errorMessage = "Certains champs obligatoires ne répondent pas aux critères attendus, veuillez vérifier.";
        }
      },
      async ImportKML() {
        this.sectionPath = GmapsService.convertKmlCoordinates(this.importKMLText);
        await this.save();
        this.dialogImportKML = false;
      },
    },
    watch: {
      "section.direction_info"(_o, _n) {
        SectionsService.actions.updateSection(this.section);
      },
      reloadData(_o, _n) {
        if (this.reloadData) {
          this.errorMessage = "";
          this.isModification = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  #gmap {
    height: 500px;
  }
</style>
